import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { stock as api } from '../services/api';
import FiltresPanel from './FiltresPanel';
import { usePanneau, COULEURS, basculer } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles, sectionsGroupes, basculerGroupe } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';
import './Stock.css';

// ─── VN / VO — Évolution du stock (Auto Antilles, 2026-09-30) ────────────────
// Deux blocs (décision du 30/09) :
//   1. PAR CONCESSION — maille Concessions / Concessions groupées / Plaque, en
//      véhicules ou en M€ à une décimale (sélecteur). Chiffres OFFICIELS Datacar depuis
//      janvier 2024, photo du jour pour le mois en cours, reconstitués avant
//      (en véhicules seulement). Cliquer une concession filtre la page.
//   2. LE DÉTAIL — marque, modèle (VN), âge, type de stock : positions
//      reconstituées (le fichier officiel ne porte pas ce détail), avec une
//      ligne « sorties sans vente » pour retomber sur le total officiel.
// Les mois s'arrêtent en fin de mois ; le premier est fin septembre 2023,
// premier mois où les ventes de toutes les sociétés sont connues.

const TITRE = { vn: 'VN — Évolution du stock', vo: 'VO — Évolution du stock' };
const DIMS = [
  { c: 'marque', l: 'Marque', cle: 'marques' },
  { c: 'modele', l: 'Modèle', cle: 'modeles', vnSeul: true },
  { c: 'age', l: 'Âge de stock', cle: 'ages' },
  { c: 'type', l: 'Type de stock' },
];
const PERIODES = [{ code: '12', nom: '12 derniers mois' }, { code: '24', nom: '24 derniers mois' }, { code: 'tout', nom: 'Tout l\'historique (depuis sept. 2023)' }];
const LIB_FILTRE = { marques: 'Marque', modeles: 'Modèle', ages: 'Âge' };
const SEP = '|';
const nf = new Intl.NumberFormat('fr-FR');
const moisCourt = (ym) => new Date(`${ym}-15`).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
const moisLong = (ym) => (ym ? new Date(`${ym}-15`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '');
const jj = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const signe = (v) => (v > 0 ? `+${nf.format(v)}` : nf.format(v));
// Valeurs en M€ à une décimale (demande du 30/09), dans les cellules comme en tête.
const mE = (v) => (v == null ? '—' : `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v / 1e6)} M€`);
const SOURCE = { officiel: 'chiffre officiel Datacar', photo: 'photo du jour', reconstitue: 'reconstitué (avant le fichier officiel)' };

export default function StockEvolution({ type = 'vn' }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, basculerPanneau] = usePanneau();
  const [dim, setDim] = useState('marque');
  const [unite, setUnite] = useState('n');   // 'n' véhicules | 'e' euros (bloc 1)
  const maille = useRegroupement();
  const get = (k) => params.get(k) || '';
  const periode = ['12', '24', 'tout'].includes(get('periode')) ? get('periode') : '12';
  const cleFiltres = ['societes', 'marques', 'modeles', 'ages'].map(get).join('§');

  useEffect(() => {
    let actif = true; setErreur('');
    api.evolution({ type, dimension: dim, periode, societes: get('societes'), marques: get('marques'), modeles: get('modeles'), ages: get('ages') })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Rapport indisponible'); });
    return () => { actif = false; };
  }, [type, dim, periode, cleFiltres]);   // eslint-disable-line react-hooks/exhaustive-deps

  const setFiltre = (cles) => {
    const next = new URLSearchParams(params);
    Object.entries(cles).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const societesUrl = get('societes');
  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societesDispo.map((s) => s.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societesDispo]);
  const couleurDe = useMemo(() => Object.fromEntries(societesDispo.map((s, i) => [s.code, COULEURS[i % COULEURS.length]])), [societesDispo]);
  const toggleSociete = (code) => {
    const set = new Set(selected); set.has(code) ? set.delete(code) : set.add(code);
    const l = [...set];
    setFiltre({ societes: l.length === 0 ? '__aucune__' : l.length === societesDispo.length ? '' : l.join(',') });
  };
  const selDe = (cle) => (get(cle) ? get(cle).split(SEP) : []);
  const basculerCle = (cle, val) => {
    const l = selDe(cle); const n = l.includes(val) ? l.filter((x) => x !== val) : [...l, val];
    setFiltre({ [cle]: n.join(SEP) });
  };
  const actifs = ['marques', 'modeles', 'ages'].flatMap((cle) => selDe(cle).map((v) => ({
    code: `${cle}${SEP}${v}`, nom: `${LIB_FILTRE[cle]} : ${cle === 'ages' ? (data?.labelsAge || [])[Number(v)] || v : v}`,
  })));
  const nbActifs = (societesUrl ? 1 : 0) + actifs.length + (periode !== '12' ? 1 : 0);

  const Panneau = (
    <FiltresPanel ouvert={ouvert} onToggle={basculerPanneau}
      choix={{ titre: 'Période', valeurs: PERIODES, valeur: periode, onChoisir: (c) => setFiltre({ periode: c === '12' ? '' : c }) }}
      societesDispo={societesDispo} societesActives={selected} onToggleSociete={toggleSociete}
      onToutesSocietes={() => setFiltre({ societes: '' })} onAucuneSociete={() => setFiltre({ societes: '__aucune__' })}
      groupes={sectionsGroupes(societesDispo)} onToggleGroupe={(g) => setFiltre({ societes: basculerGroupe(g, selected, societesDispo) })}
      categoriesDispo={actifs} categoriesActives={actifs.map((a) => a.code)} titreCategories="Détail retenu" categoriesApres
      onToggleCategorie={(code) => { const [cle, ...v] = code.split(SEP); basculerCle(cle, v.join(SEP)); }}
      couleurDe={couleurDe} nbActifs={nbActifs}
      onReset={() => setFiltre({ societes: '', marques: '', modeles: '', ages: '', periode: '' })} />
  );

  const mois = data?.mois || [];
  const yms = mois.map((m) => m.ym);
  // Bloc 1 : toutes les concessions de l'habilitation (c'est la liste où l'on
  // choisit — règle 4), regroupées selon la maille.
  const feuilles = useMemo(() => data?.concessions || [], [data]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const arbre = useMemo(() => {
    const sommer = (membres) => {
      const valeurs = {};
      membres.forEach((m) => Object.entries(m.valeurs).forEach(([ym, x]) => {
        const a = valeurs[ym] || { v: 0, e: 0 };
        valeurs[ym] = { v: a.v + x.v, e: a.e == null || x.e == null ? null : a.e + x.e };
      }));
      return { valeurs };
    };
    return construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => x.valeurs[yms[yms.length - 1]]?.v || 0);
  }, [feuilles, maille.niveau, maille.ouverts]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const k = data.kpis;
  const premier = yms[0]; const dernier = yms[yms.length - 1];
  const val = (x) => (!x ? null : unite === 'n' ? x.v : x.e);
  const fmt = (v) => (v == null ? '—' : unite === 'n' ? (v ? nf.format(v) : '·') : mE(v));
  const fmtEv = (a, b) => (a == null || b == null ? '—' : unite === 'n' ? (a - b ? signe(a - b) : '·') : `${a - b > 0 ? '+' : ''}${mE(a - b)}`);
  const totalConc = (ym) => {
    const l = feuilles.filter((c) => selected.includes(c.code)).map((c) => c.valeurs[ym]);
    return l.reduce((a, x) => ({ v: a.v + x.v, e: a.e == null || x.e == null ? null : a.e + x.e }), { v: 0, e: 0 });
  };
  const D = DIMS.find((d) => d.c === dim) || DIMS[0];
  const cleLigne = (l) => (dim === 'age' ? String(l.cle) : l.cle);
  const det = data.detail || { lignes: [], total: {} };
  const ecartVisible = det.ecart && yms.some((ym) => det.ecart[ym]);
  const officiel = k?.officielJusqua;

  const EnTeteMois = () => yms.map((ym, i) => (
    <th key={ym} title={SOURCE[mois[i].source]} className={mois[i].source === 'officiel' ? '' : 'st-mois-autre'}>
      {moisCourt(ym)}{mois[i].source === 'photo' ? ' *' : mois[i].source === 'reconstitue' ? ' °' : ''}
    </th>
  ));

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>{TITRE[type]}</h1></div>
          <div className="vt-asof"><b>{moisLong(premier)} → {moisLong(dernier)}</b><br />fin de mois · dernier point au {jj(k?.date)}</div>
        </div>

        {!k ? (
          <div className="bi-empty"><h2>Aucune donnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
        ) : (
          <>
            <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
              <div className="vt-kpi"><div className="l">Stock au {jj(k.date)}</div><div className="v">{nf.format(k.fin)}</div>
                <div className="d">{k.n1 != null ? <>{signe(k.fin - k.n1)} vs fin {moisLong(k.ymN1)} ({k.n1 ? signe(Math.round(((k.fin - k.n1) / k.n1) * 100)) : '—'} %)</> : 'pas de point un an avant'}</div></div>
              <div className="vt-kpi"><div className="l">Valeur d'achat HT</div><div className="v">{mE(k.valeur)}</div>
                <div className="d">{k.valeurN1 != null && k.valeur != null ? `${k.valeur - k.valeurN1 > 0 ? '+' : ''}${mE(k.valeur - k.valeurN1)} vs fin ${moisLong(k.ymN1)}` : 'photo du jour'}</div></div>
              <div className="vt-kpi"><div className="l">Plus de 365 jours</div><div className="v">{nf.format(k.plus365)}</div>
                <div className="d">{k.fin ? Math.round((k.plus365 / k.fin) * 100) : 0} % du stock</div></div>
              <div className="vt-kpi"><div className="l">Point haut · point bas</div><div className="v">{k.haut ? nf.format(k.haut.n) : '—'} · {k.bas ? nf.format(k.bas.n) : '—'}</div>
                <div className="d">{k.haut && k.bas ? `fin ${moisLong(k.haut.ym)} · fin ${moisLong(k.bas.ym)}` : ''}</div></div>
            </div>

            {/* ── Bloc 1 : par concession ──────────────────────────────── */}
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Stock de fin de mois par concession</h2>
                <div className="st-tools">
                  <span className="hint">{officiel ? `chiffres officiels Datacar jusqu'à fin ${moisLong(officiel)}` : 'chiffres reconstitués'} · * photo du jour · ° reconstitué, en véhicules seulement</span>
                  <div className="st-seg">
                    <button type="button" className={unite === 'n' ? 'on' : ''} onClick={() => setUnite('n')}>véhicules</button>
                    <button type="button" className={unite === 'e' ? 'on' : ''} onClick={() => setUnite('e')}>M€</button>
                  </div>
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap">
                <table className="vt-tbl tbl-dense">
                  <thead><tr><th className="l">Concession</th><EnTeteMois /><th title={`Écart entre fin ${moisLong(premier)} et le dernier point`}>Évol.</th></tr></thead>
                  <tbody>
                    {arbre.map((l) => {
                      const cible = l.feuille ? l.code : (l.membres || []).join(',');
                      return (
                        <tr key={l.cle} className={`tbl-clic rg-n${l.niveau || 0}${societesUrl === cible ? ' actif' : ''}`}
                            onClick={() => setFiltre({ societes: basculer(societesUrl, cible) })}>
                          <td className="l">
                            <span className="rg-in" style={{ width: (l.niveau || 0) * 14 }} />
                            {l.feuille ? <span className="rg-chev vide" /> : (
                              <button type="button" className={`rg-btn${maille.ouverts.has(l.cle) ? ' on' : ''}`} aria-label="Déplier"
                                title={maille.ouverts.has(l.cle) ? 'Replier' : 'Voir le détail par concession'}
                                onClick={(e) => { e.stopPropagation(); maille.basculer(l.cle); }}>▶</button>
                            )}
                            {l.libelle}
                            {!l.feuille && <span className="rg-nb">{l.nbEnfants}</span>}
                          </td>
                          {yms.map((ym) => <td key={ym}>{fmt(val(l.valeurs[ym]))}</td>)}
                          <td>{fmtEv(val(l.valeurs[dernier]), val(l.valeurs[premier]))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr>
                    <td className="l">Total{selected.length < societesDispo.length ? ' (sélection)' : ''}</td>
                    {yms.map((ym) => <td key={ym}>{fmt(val(totalConc(ym)))}</td>)}
                    <td>{fmtEv(val(totalConc(dernier)), val(totalConc(premier)))}</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>

            {/* ── Bloc 2 : le détail ───────────────────────────────────── */}
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Détail par {D.l.toLowerCase()}</h2>
                <div className="st-tools">
                  <span className="hint">
                    en véhicules · positions reconstituées (stock actuel + ventes postérieures){selected.length < societesDispo.length ? ' · concessions retenues dans le bloc du dessus' : ''}
                    {det.filtre ? ' · écart au total officiel non ventilable sous ce filtre' : ''}
                  </span>
                  <div className="st-seg">
                    {DIMS.filter((d) => !d.vnSeul || type === 'vn').map((d) => (
                      <button key={d.c} type="button" className={dim === d.c ? 'on' : ''} onClick={() => setDim(d.c)}>{d.l}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap vt-borne">
                <table className="vt-tbl tbl-dense">
                  <thead><tr><th className="l">{D.l}</th><EnTeteMois /><th>Évol.</th></tr></thead>
                  <tbody>
                    {det.lignes.map((l) => {
                      const ev = (l.valeurs[dernier] || 0) - (l.valeurs[premier] || 0);
                      const actif = D.cle && selDe(D.cle).includes(cleLigne(l));
                      return (
                        <tr key={l.cle} className={`${D.cle ? 'tbl-clic' : ''}${actif ? ' actif' : ''}`}
                            onClick={D.cle ? () => basculerCle(D.cle, cleLigne(l)) : undefined}>
                          <td className="l">{l.libelle}</td>
                          {yms.map((ym) => <td key={ym}>{l.valeurs[ym] ? nf.format(l.valeurs[ym]) : '·'}</td>)}
                          <td>{ev ? signe(ev) : '·'}</td>
                        </tr>
                      );
                    })}
                    {ecartVisible && (
                      <tr className="sous" title="Véhicules sortis du stock sans facture de vente (transfert, retour constructeur…) : la reconstitution ne les voit pas ; l'écart au total officiel est porté ici.">
                        <td className="l">Sorties sans vente (non ventilées)</td>
                        {yms.map((ym) => <td key={ym}>{det.ecart[ym] ? signe(det.ecart[ym]) : '·'}</td>)}
                        <td />
                      </tr>
                    )}
                  </tbody>
                  <tfoot><tr>
                    <td className="l">Total</td>
                    {yms.map((ym) => <td key={ym}>{nf.format((det.total[ym] || 0) + (det.ecart ? det.ecart[ym] || 0 : 0))}</td>)}
                    <td>{signe(((det.total[dernier] || 0) + (det.ecart ? det.ecart[dernier] || 0 : 0)) - ((det.total[premier] || 0) + (det.ecart ? det.ecart[premier] || 0 : 0)))}</td>
                  </tr></tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
      {Panneau}
    </>
  );
}
